import { useEffect, useState, useRef } from "react";
import HeroMockup from './HeroMockup';
import WhatsAppLink from '../global/WhatsAppLink';
import CalendlyLink from '../global/CalendlyLink';
import { CTA_IDS, CTA_LOCATIONS } from '../../lib/analytics.constants';

const WORDS_ES = [
    { text: "Del",      gradient: false },
    { text: "concepto", gradient: false },
    { text: "al",       gradient: false },
    { text: "producto", gradient: true  },
];

const WORDS_EN = [
    { text: "From",     gradient: false },
    { text: "concept",  gradient: false },
    { text: "to",       gradient: false },
    { text: "product",  gradient: true  },
];

function readPageLang() {
    const htmlLang = (document.documentElement.lang || 'es').slice(0, 2).toLowerCase();
    if (htmlLang === 'en') return 'en';
    const googtrans = document.cookie.match(/googtrans=\/[^/]+\/(\w+)/);
    if (googtrans?.[1] === 'en') return 'en';
    return 'es';
}

const SPACING     = 80;
const BH_SCALE    = 160;  // black hole "event horizon" scale — lower = stronger pull
const LERP        = 0.10;

export default function HeroBanner() {
    const [lang,         setLang]         = useState('es');
    const [revealed,     setRevealed]     = useState(WORDS_ES.map(() => false));
    const [showSubtitle, setShowSubtitle] = useState(false);
    const [showCta,      setShowCta]      = useState(false);
    const [scrollY,      setScrollY]      = useState(0);
    const words = lang === 'en' ? WORDS_EN : WORDS_ES;

    const canvasRef      = useRef(null);
    const targetMouse    = useRef({ x: -9999, y: -9999 });
    const smoothMouse    = useRef({ x: -9999, y: -9999 });
    const animRef        = useRef(null);

    // Elfsight / page language — keep the headline out of auto-translate
    useEffect(() => {
        const sync = () => setLang(readPageLang());
        sync();
        const html = document.documentElement;
        const obs = new MutationObserver(sync);
        obs.observe(html, { attributes: true, attributeFilter: ['lang', 'class'] });
        window.addEventListener('languagechange', sync);
        return () => {
            obs.disconnect();
            window.removeEventListener('languagechange', sync);
        };
    }, []);

    // Word cascade on load
    useEffect(() => {
        const timers = [];
        WORDS_ES.forEach((_, i) => {
            timers.push(
                setTimeout(() => {
                    setRevealed(prev => {
                        const next = [...prev];
                        next[i] = true;
                        return next;
                    });
                }, 120 + i * 200)
            );
        });
        const base = 120 + WORDS_ES.length * 200;
        timers.push(setTimeout(() => setShowSubtitle(true), base + 100));
        timers.push(setTimeout(() => setShowCta(true),      base + 280));
        return () => timers.forEach(clearTimeout);
    }, []);


    // Parallax on scroll
    useEffect(() => {
        const onScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Canvas gravity grid
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width  = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };

        // Track mouse relative to canvas
        const onMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            targetMouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        };
        const onMouseLeave = () => {
            targetMouse.current = { x: -9999, y: -9999 };
        };

        const draw = () => {
            // Smooth-follow mouse (canvas-relative + viewport)
            const sm = smoothMouse.current;
            const tm = targetMouse.current;
            sm.x += (tm.x - sm.x) * LERP;
            sm.y += (tm.y - sm.y) * LERP;

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = 'rgba(255,255,255,0.055)';
            ctx.lineWidth   = 1;

            const cols = Math.ceil(canvas.width  / SPACING) + 1;
            const rows = Math.ceil(canvas.height / SPACING) + 1;

            // Black hole convergence — ALL points pulled toward cursor
            const pts = [];
            for (let r = 0; r <= rows; r++) {
                const row = [];
                for (let c = 0; c <= cols; c++) {
                    const ox   = c * SPACING;
                    const oy   = r * SPACING;
                    const dx   = ox - sm.x;
                    const dy   = oy - sm.y;
                    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                    // Inverse-power falloff: all points converge, strongest at center
                    const pull = 1 / (1 + Math.pow(dist / BH_SCALE, 1.5));
                    row.push({
                        px: ox + (sm.x - ox) * pull,
                        py: oy + (sm.y - oy) * pull,
                    });
                }
                pts.push(row);
            }

            // Horizontal lines
            for (let r = 0; r <= rows; r++) {
                ctx.beginPath();
                ctx.moveTo(pts[r][0].px, pts[r][0].py);
                for (let c = 1; c <= cols; c++) {
                    ctx.lineTo(pts[r][c].px, pts[r][c].py);
                }
                ctx.stroke();
            }

            // Vertical lines
            for (let c = 0; c <= cols; c++) {
                ctx.beginPath();
                ctx.moveTo(pts[0][c].px, pts[0][c].py);
                for (let r = 1; r <= rows; r++) {
                    ctx.lineTo(pts[r][c].px, pts[r][c].py);
                }
                ctx.stroke();
            }


            animRef.current = requestAnimationFrame(draw);
        };

        resize();
        window.addEventListener('resize',     resize);
        window.addEventListener('mousemove',  onMouseMove);
        window.addEventListener('mouseleave', onMouseLeave);
        draw();

        return () => {
            cancelAnimationFrame(animRef.current);
            window.removeEventListener('resize',     resize);
            window.removeEventListener('mousemove',  onMouseMove);
            window.removeEventListener('mouseleave', onMouseLeave);
        };
    }, []);

    return (
        <div className="relative w-full overflow-hidden">

            {/* Gravity grid canvas — dark mode only */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none opacity-0 dark:opacity-100"
            />

            {/* Green glow — offset right toward mockup */}
            <div
                className="absolute inset-x-0 top-0 h-[600px] pointer-events-none opacity-0 dark:opacity-100"
                style={{ background: 'radial-gradient(ellipse 65% 52% at 68% -5%, rgba(17,179,109,0.16), transparent 72%)' }}
            />

            {/* Content */}
            <div
                className="relative w-full max-w-screen-xl mx-auto px-4 md:px-8 pt-28 md:pt-40 pb-12"
                style={{ transform: `translateY(${scrollY * 0.12}px)` }}
            >
                <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-4">

                    {/* ── Left: text + CTAs ── */}
                    <div className="flex-1 text-center md:text-left">

                        <h1 translate="no" className="ignore-translation text-5xl md:text-6xl lg:text-7xl font-thin leading-[1.1] tracking-tighter mb-8 text-[#0b2b40] dark:text-white">
                            {words.map((word, i) => (
                                <span
                                    key={i}
                                    className="inline-block mr-[0.22em] last:mr-0"
                                    style={{
                                        opacity:    revealed[i] ? 1 : 0,
                                        transform:  revealed[i] ? 'translateY(0px)' : 'translateY(28px)',
                                        transition: 'opacity 0.7s cubic-bezier(0.32,0.72,0,1), transform 0.7s cubic-bezier(0.32,0.72,0,1)',
                                    }}
                                >
                                    {word.gradient
                                        ? <span className="text-gradient text-short">{word.text}</span>
                                        : word.text
                                    }
                                </span>
                            ))}
                        </h1>

                        <p
                            className="text-xl md:text-2xl text-slate-500 dark:text-slate-400 font-light mb-10 max-w-xl leading-relaxed mx-auto md:mx-0"
                            style={{
                                opacity:   showSubtitle ? 1 : 0,
                                transform: showSubtitle ? 'translateY(0px)' : 'translateY(20px)',
                                transition: 'opacity 0.65s cubic-bezier(0.32,0.72,0,1), transform 0.65s cubic-bezier(0.32,0.72,0,1)',
                            }}
                        >
                            Desarrollamos software a medida, apps y plataformas para empresas y startups
                        </p>

                        <div
                            className="flex flex-col sm:flex-row items-center justify-center md:items-start md:justify-start gap-4"
                            style={{
                                opacity:   showCta ? 1 : 0,
                                transform: showCta ? 'translateY(0px)' : 'translateY(20px)',
                                transition: 'opacity 0.6s cubic-bezier(0.32,0.72,0,1), transform 0.6s cubic-bezier(0.32,0.72,0,1)',
                            }}
                        >
                            <WhatsAppLink
                                ctaId={CTA_IDS.HERO_WHATSAPP}
                                ctaLocation={CTA_LOCATIONS.HERO}
                                message="Hola, me interesa saber más sobre sus servicios"
                                className="inline-flex items-center gap-1.5 px-7 py-3.5 font-semibold rounded-full bg-[#11b36d] text-white shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#0ea060] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-200"
                            >
                                Escribinos
                            </WhatsAppLink>
                            <CalendlyLink
                                ctaId={CTA_IDS.HERO_CALENDLY}
                                ctaLocation={CTA_LOCATIONS.HERO}
                                className="group inline-flex items-center gap-3 pl-7 pr-2 py-2 rounded-full border border-gray-200 dark:border-white/[0.12] text-slate-600 dark:text-slate-300 font-medium text-base hover:border-[#11b36d]/50 hover:text-[#11b36d] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
                            >
                                Agendar una sesión
                                <span className="w-9 h-9 rounded-full bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-px group-hover:bg-[#11b36d]/10 transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </span>
                            </CalendlyLink>
                        </div>

                        <p
                            className="text-xs text-slate-400 dark:text-slate-500 mt-8 transition-opacity duration-700"
                            style={{ opacity: showCta ? 1 : 0 }}
                        >
                            Aliados Tecnológicos · Software a medida · Apps móviles · SaaS · Automatización
                        </p>
                    </div>

                    {/* ── Right: mockup ── */}
                    <div
                        className="w-full lg:w-[480px] xl:w-[520px] h-[320px] sm:h-[380px] lg:h-[460px] shrink-0"
                        style={{
                            opacity:   showSubtitle ? 1 : 0,
                            transform: showSubtitle ? 'translateY(0px)' : 'translateY(24px)',
                            transition: 'opacity 0.75s cubic-bezier(0.32,0.72,0,1) 0.15s, transform 0.75s cubic-bezier(0.32,0.72,0,1) 0.15s',
                        }}
                    >
                        <HeroMockup scrollY={scrollY} />
                    </div>

                </div>
            </div>
        </div>
    );
}
